import type {
    User,
    GameState,
    SubmitGuessResponse,
    ApiResponse,
    PuzzleInfo,
    CollabInboxResponse
} from '../types';

import { Capacitor } from '@capacitor/core';

// Production API URL
const PRODUCTION_API = 'https://hotorcold.io';

// In live reload mode (dev): Vite proxy handles API routing, so use relative paths
// In bundled mode (prod/native): Need to specify the production API URL
const isNative = Capacitor.isNativePlatform();
const API_BASE = isNative ? PRODUCTION_API : '';

// Debug: Log the API configuration on load
console.log('[API] Native platform:', isNative, '| API_BASE:', API_BASE || '(relative)');

/**
 * Custom error for archived puzzle access
 */
export class ArchivedPuzzleError extends Error {
    puzzleId: string;
    unlockCost: number;

    constructor(message: string, puzzleId: string, unlockCost: number = 100) {
        super(message);
        this.name = 'ArchivedPuzzleError';
        this.puzzleId = puzzleId;
        this.unlockCost = unlockCost;
    }
}

/**
 * Custom error for threshold-locked puzzle access
 */
export class ThresholdLockedError extends Error {
    puzzleId: string;
    requiredThreshold: number;
    currentCompletion: number;
    mode: string;

    constructor(
        message: string,
        puzzleId: string,
        requiredThreshold: number,
        currentCompletion: number,
        mode: string
    ) {
        super(message);
        this.name = 'ThresholdLockedError';
        this.puzzleId = puzzleId;
        this.requiredThreshold = requiredThreshold;
        this.currentCompletion = currentCompletion;
        this.mode = mode;
    }
}

/**
 * Fetch wrapper with error handling and credentials
 */
async function fetchApi<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    console.log('[API] Fetching:', url);

    const response = await fetch(url, {
        ...options,
        // Use 'include' for native platforms (cross-origin requests to production API)
        // Use 'same-origin' for web (requests go through Vite proxy or same domain)
        credentials: isNative ? 'include' : 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));

        // Check for threshold-locked puzzle error
        if (response.status === 403 && error.is_locked_by_threshold) {
            const puzzleIdMatch = endpoint.match(/\/api\/state\/([^\/]+)/);
            const puzzleId = puzzleIdMatch?.[1] || '';
            throw new ThresholdLockedError(
                error.error || error.message || 'This puzzle is locked',
                puzzleId,
                error.required_threshold || 0,
                error.current_completion || 0,
                error.mode || 'classic'
            );
        }

        // Check for archived puzzle error
        if (response.status === 403 && error.is_archived) {
            // Extract puzzle ID from endpoint if available
            const puzzleIdMatch = endpoint.match(/\/api\/state\/([^\/]+)/);
            const puzzleId = puzzleIdMatch?.[1] || '';
            throw new ArchivedPuzzleError(
                error.error || error.message || 'This puzzle is archived',
                puzzleId,
                error.unlock_cost || 100
            );
        }

        throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    return response.json();
}


// ============ User API ============

export async function fetchProfile(): Promise<User> {
    return fetchApi<User>('/api/me');
}

export async function login(username: string, password: string): Promise<ApiResponse<User>> {
    return fetchApi<ApiResponse<User>>('/api/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
    });
}

export interface SignupResponse extends ApiResponse<User> {
    coins?: number;  // Welcome bonus coins for new users
}

export async function signup(
    username: string,
    email: string,
    password: string
): Promise<SignupResponse> {
    return fetchApi<SignupResponse>('/api/signup', {
        method: 'POST',
        body: JSON.stringify({ username, email, password }),
    });
}

export async function logout(): Promise<ApiResponse> {
    return fetchApi<ApiResponse>('/api/logout', { method: 'POST' });
}

// ============ Password Reset API ============

export async function requestPasswordReset(email: string): Promise<ApiResponse> {
    return fetchApi<ApiResponse>('/api/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

export async function verifyResetToken(token: string): Promise<{ valid: boolean }> {
    return fetchApi<{ valid: boolean }>('/api/password-reset/verify', {
        method: 'POST',
        body: JSON.stringify({ token }),
    });
}

export async function confirmPasswordReset(token: string, password: string): Promise<ApiResponse> {
    return fetchApi<ApiResponse>('/api/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
    });
}

// ============ Game API ============

interface StateWrapper {
    state: GameState;
}

export async function fetchGameState(puzzleId: string): Promise<GameState> {
    const response = await fetchApi<StateWrapper>(`/api/state/${puzzleId}`);
    return response.state;
}

export async function submitGuess(
    puzzleId: string,
    guess: string
): Promise<SubmitGuessResponse> {
    return fetchApi<SubmitGuessResponse>('/api/submit', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, guess }),
    });
}

export async function requestHint(puzzleId: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/hint', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

export async function giveUp(puzzleId: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/giveup', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

export async function revealGemLetter(puzzleId: string, letter: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/classic/reveal-gem-letter', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, letter }),
    });
}

export async function useCompass(puzzleId: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/classic/compass', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

// ============ Store API ============

export interface StoreItem {
    id: string;
    name: string;
    description: string;
    icon: string;
    price: number;
    mode: string;
    quantity: number;
}

export interface StoreItemsResponse {
    items: StoreItem[];
}

export interface PurchaseResponse extends ApiResponse {
    coins_remaining?: number;
    inventory?: Record<string, number>;
}

export async function fetchStoreItems(): Promise<StoreItemsResponse> {
    return fetchApi<StoreItemsResponse>('/api/store/items');
}

export async function purchaseStoreItem(itemType: string): Promise<PurchaseResponse> {
    return fetchApi<PurchaseResponse>('/api/store/purchase', {
        method: 'POST',
        body: JSON.stringify({ item_type: itemType }),
    });
}

// ============ Bridge API ============

export async function bridgeUndo(puzzleId: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/bridge/undo', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

export async function bridgeReset(puzzleId: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/bridge/reset', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

export interface BridgeClueResponse {
    success: boolean;
    message: string;
    scrambled_letters?: string[];
    word_length?: number;
    penalty_guesses?: number;
    coins_remaining?: number;
}

export async function buyBridgeClue(puzzleId: string): Promise<BridgeClueResponse> {
    return fetchApi<BridgeClueResponse>('/api/bridge/clue', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

export async function solveBridgeClue(
    puzzleId: string,
    answer: string
): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/bridge/clue/solve', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, answer }),
    });
}

export async function cancelBridgeClue(puzzleId: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/bridge/clue/cancel', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

// ============ Flip API ============

export async function buyFlipClue(
    puzzleId: string,
    targetIndex?: number
): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/flip/clue', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, target_index: targetIndex }),
    });
}

export async function answerThunderword(
    puzzleId: string,
    answerIndex: number
): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/flip/thunderword', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, answer_index: answerIndex }),
    });
}

export async function buyFlipLetter(puzzleId: string, letter?: string): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/flip/letter', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, letter }),
    });
}

export async function buyEchoHint(puzzleId: string, targetIndex: number): Promise<ApiResponse<GameState>> {
    return fetchApi<ApiResponse<GameState>>('/api/flip/echo', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId, target_index: targetIndex }),
    });
}

export async function getAvailableLetters(puzzleId: string): Promise<{ letters: Record<string, number> }> {
    return fetchApi<{ letters: Record<string, number> }>(`/api/flip/letters?puzzle_id=${puzzleId}`);
}

// ============ Blitz API ============

export async function fetchBlitzState(): Promise<{
    puzzle_id?: string;
    session_id?: string;
    phase?: string;
    time_remaining: number;
    round_duration?: number;
    break_duration?: number;
    paused?: boolean;
}> {
    return fetchApi('/api/blitz/state');
}

export async function fetchBlitzLeaderboard(): Promise<{
    players: Array<{
        username: string;
        avatar?: string;
        position?: number;
        best_rank?: number;
        guess_count?: number;
    }>;
    round_number: number;
    winner?: string;
    answer?: string;
}> {
    return fetchApi('/api/blitz/leaderboard');
}

// ============ Puzzle List API ============

export async function fetchPuzzleList(mode?: string): Promise<{ puzzles: PuzzleInfo[]; default: string | null }> {
    const url = mode ? `/api/puzzles?mode=${mode}` : '/api/puzzles';
    return fetchApi(url);
}

export async function unlockArchivedPuzzle(puzzleId: string): Promise<{ success: boolean; message: string; coins_remaining?: number }> {
    return fetchApi('/api/puzzle/unlock', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
}

export async function fetchUnlockedSongs(): Promise<{ songs: string[] }> {
    return fetchApi('/api/unlocked-songs');
}

export async function fetchBasePlaylist(): Promise<{ songs: string[] }> {
    return fetchApi<{ songs: string[] }>('/api/playlist');
}

// ============ Collab API ============

export async function sendCollabRequest(
    targetUsername: string,
    puzzleId: string
): Promise<ApiResponse> {
    return fetchApi<ApiResponse>('/api/collab/request', {
        method: 'POST',
        body: JSON.stringify({
            username: targetUsername,
            puzzle_id: puzzleId
        }),
    });
}

export async function respondToCollabRequest(
    requestId: number,
    accept: boolean
): Promise<ApiResponse> {
    return fetchApi<ApiResponse>('/api/collab/respond', {
        method: 'POST',
        body: JSON.stringify({ request_id: requestId, action: accept ? 'accept' : 'reject' }),
    });
}

export async function fetchCollabInbox(): Promise<CollabInboxResponse> {
    return fetchApi<CollabInboxResponse>('/api/collab/inbox');
}

// ============ Avatar & Profile ============



// ============ Avatar & Profile ============

export interface AvatarInfo {
    path: string;
    locked: boolean;
    unlock_requirement?: string;
}

export interface AvatarCategory {
    name: string;
    id: string;
    avatars: (string | AvatarInfo)[];
}

export async function fetchAvatars(): Promise<AvatarCategory[]> {
    const res = await fetchApi<{ categories: AvatarCategory[] }>('/api/avatars');
    return res.categories || [];
}

export async function updateAvatar(avatar: string): Promise<{ success: boolean; user: User }> {
    return fetchApi<{ success: boolean; user: User }>('/api/user/avatar', {
        method: 'POST',
        body: JSON.stringify({ avatar }),
    });
}

export async function updateEmail(email: string): Promise<{ success: boolean; message: string; email?: string; error?: string }> {
    return fetchApi<{ success: boolean; message: string; email?: string; error?: string }>('/api/user/email', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

export async function updatePassword(
    currentPassword: string,
    newPassword: string
): Promise<{ success: boolean; message: string; error?: string }> {
    return fetchApi<{ success: boolean; message: string; error?: string }>('/api/user/password', {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
}

// ============ Friends ============

export interface Friend {
    id: number;
    username: string;
    avatar?: string;
    friendship_status?: 'friends' | 'pending_sent' | 'pending_received' | 'none';
}

export async function searchFriends(query: string): Promise<Friend[]> {
    const res = await fetchApi<{ users: Friend[] }>(`/api/friends/search?q=${encodeURIComponent(query)}`);
    return res.users;
}

export async function getFriends(): Promise<Friend[]> {
    const res = await fetchApi<{ friends: Friend[] }>('/api/friends');
    return res.friends;
}

export async function getFriendRequests(): Promise<any[]> { // Type this better if possible
    const res = await fetchApi<{ requests: any[] }>('/api/friends/requests');
    return res.requests;
}

export async function sendFriendRequest(userId: number): Promise<{ success: boolean; message: string }> {
    return fetchApi('/api/friends/request', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
    });
}

export async function respondToFriendRequest(requestId: number, accept: boolean): Promise<{ success: boolean; message: string }> {
    return fetchApi('/api/friends/respond', {
        method: 'POST',
        body: JSON.stringify({ request_id: requestId, accept }),
    });
}

export async function removeFriend(friendId: number): Promise<{ success: boolean; message: string }> {
    return fetchApi('/api/friends/remove', {
        method: 'POST',
        body: JSON.stringify({ friend_id: friendId }),
    });
}

// ============ Inbox ============

export interface InboxMessage {
    id: number;
    title: string;
    body: string;
    type: string;
    created_at: string;
    read: boolean;
    data?: any;
}

export async function fetchInbox(): Promise<{ messages: InboxMessage[]; requests: any[] }> {
    return fetchApi('/api/collab/inbox');
}

export async function markInboxRead(messageIds: number[]): Promise<{ success: boolean }> {
    return fetchApi('/api/inbox/read', {
        method: 'POST',
        body: JSON.stringify({ message_ids: messageIds }),
    });
}

export async function deleteInboxMessages(messageIds: number[]): Promise<{ success: boolean; deleted_count: number }> {
    return fetchApi('/api/inbox/delete', {
        method: 'POST',
        body: JSON.stringify({ message_ids: messageIds }),
    });
}

// ============ System Status ============

export interface SystemStatus {
    version: number;
    title: string;
    message: string;
    date?: string;
    swipe_hidden?: boolean;
}

export async function fetchSystemStatus(): Promise<SystemStatus> {
    return fetchApi<SystemStatus>('/api/system/status');
}

export async function updateSystemStatus(
    version: number,
    title: string,
    message: string,
    date?: string
): Promise<{ success: boolean; message?: string }> {
    return fetchApi('/api/admin/system/status', {
        method: 'POST',
        body: JSON.stringify({ version, title, message, date }),
    });
}

// ============ Leaderboard ============

export interface LeaderboardEntry {
    rank: number;
    username: string;
    avatar?: string;
    percentage: number;
    coins: number;
}

export interface LeaderboardData {
    leaderboards: {
        classic: LeaderboardEntry[];
        flip: LeaderboardEntry[];
        bridge: LeaderboardEntry[];
    };
}

export async function fetchLeaderboard(): Promise<LeaderboardData> {
    return fetchApi<LeaderboardData>('/api/leaderboard');
}

// ============ Blitz Chat WebSocket ============

/**
 * Get the WebSocket URL for Blitz chat
 * WebSocket is used instead of SSE for better compatibility with DigitalOcean App Platform
 */
export function getBlitzChatWebSocketUrl(sessionId: string): string {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = isNative ? 'hotorcold.io' : window.location.host;
    return `${wsProtocol}//${host}/ws/blitz/chat?session_id=${sessionId}`;
}

/**
 * @deprecated Use WebSocket connection instead
 * Kept for backwards compatibility during transition
 */
export async function sendBlitzChatMessage(
    sessionId: string,
    message: string
): Promise<{ success: boolean; message?: string }> {
    return fetchApi<{ success: boolean; message?: string }>('/api/blitz/chat', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, message }),
    });
}
